import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import { useLocalStorage } from '@passkeys/hooks';
import { useEffect, useMemo } from 'react';

import exodus from '@/ui/exodus';
import type {
  ActivityEntry,
  TransactionEntry,
} from '@/ui/flux/tx-log/selectors/create-filtered-activity.js';
import useActivity from '@/ui/hooks/use-activity.js';
import useConversion from '@/ui/hooks/use-conversion';
import useEnabledAssets from '@/ui/hooks/use-enabled-assets.js';

const STORAGE_KEY = 'analytics!firstFundId';

function isTransactionEntry(item: ActivityEntry): item is TransactionEntry {
  return 'txId' in item;
}

function useTrackFirstFund() {
  const [firstFundId, setFirstFundId] = useLocalStorage<string | null>(
    STORAGE_KEY,
    null,
  );

  const assets = useEnabledAssets();
  const assetNames = useMemo(
    () => Object.values(assets).map((asset) => asset.name),
    [assets],
  );

  // We can't reliably store if the event was tracked due to fusion's use of local storage in Passkeys.
  // As a heuristic, if a send transaction exists, we assume the event was already tracked.
  const sentActivity = useActivity({ assetNames, type: ['sent'] });
  const sendTransactionExists = useMemo(
    () =>
      sentActivity.some((activityEntry) => isTransactionEntry(activityEntry)),
    [sentActivity],
  );

  const receivedActivity = useActivity({ assetNames, type: ['received'] });
  const oldestTransaction = useMemo(
    () =>
      // This prevents unnecessary find operations on the `receivedActivity` list.
      sendTransactionExists
        ? null
        : (
            receivedActivity
              .toReversed()
              .find((activityEntry) =>
                isTransactionEntry(activityEntry),
              ) as TransactionEntry
          )?.tx,
    [receivedActivity, sendTransactionExists],
  );

  const asset = assets[oldestTransaction?.coinName ?? ''];
  const fiatConversion = useConversion(asset?.name ?? '');

  useEffect(() => {
    if (!oldestTransaction || !asset) {
      return;
    }

    // Already tracked.
    if (sendTransactionExists || firstFundId === oldestTransaction.txId) {
      return;
    }

    const eventData = {
      amount: formatAssetAmount(
        oldestTransaction.coinAmount.toDefaultNumber(),
        { useGrouping: false },
      ),
      amountUsd: fiatConversion(oldestTransaction.coinAmount).toDefaultString({
        unit: false,
      }),
      assetName: asset.name,
      network: asset.baseAsset.name,
    };

    exodus.analytics.track({ event: 'FirstFund', properties: eventData });
    setFirstFundId(oldestTransaction.txId);
  }, [
    asset,
    fiatConversion,
    firstFundId,
    oldestTransaction,
    sendTransactionExists,
    setFirstFundId,
  ]);
}

export default useTrackFirstFund;
